import React, { useMemo } from 'react'
import { DcChart, DcDiv, DcFlexBox, DcText, colors } from 'pixel-base'
import { InventoryProduct } from './types'

interface RunRateChartProps {
  data: InventoryProduct[]
  loading: boolean
  error: string | null
  onProductClick?: (product: InventoryProduct) => void
  showLineChartOnly?: boolean
  showScatterPlotOnly?: boolean
  onTitleClick?: () => void
}

const RunRateChart: React.FC<RunRateChartProps> = ({
  data,
  loading,
  error,
  onProductClick,
  showLineChartOnly = false,
  showScatterPlotOnly = false,
  onTitleClick,
}) => {
  // Transform data for line chart (simulated trend over time)
  const lineChartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        xAxis: [],
        series: [
          {
            name: 'Daily Run Rate',
            type: 'line',
            data: [],
            smooth: true,
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: colors.info },
                  { offset: 1, color: 'rgba(0, 123, 255, 0.1)' },
                ],
              },
            },
          },
        ],
      }
    }

    // Simulate 30 days of data for the top 5 products
    const topProducts = data.slice(0, 5)
    const days = Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`)

    const series = topProducts.map((product, index) => {
      const baseRate = product.daily_velocity || 1
      const data = days.map((_, dayIndex) => {
        // Simulate some variation in run rate
        const variation =
          Math.sin(dayIndex * 0.2) * 0.3 + (Math.random() * 0.2 - 0.1)
        return Math.max(0, baseRate + variation)
      })

      return {
        name: product.product_name,
        type: 'line',
        data,
        smooth: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: `hsl(${200 + index * 40}, 70%, 50%)`,
        },
      }
    })

    return {
      xAxis: days,
      series,
    }
  }, [data])

  // Transform data for scatter plot (stock vs run rate correlation)
  const scatterData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        series: [
          {
            name: 'Stock vs Run Rate',
            type: 'scatter',
            data: [],
            symbolSize: (data: number[]) => Math.sqrt(data[2]) * 2,
          },
        ],
      }
    }

    const scatterPoints = data.map((product) => [
      product.current_stock,
      product.daily_velocity || 0,
      product.days_until_stockout || 0, // Used for bubble size
      product.product_name,
    ])

    return {
      series: [
        {
          name: 'Stock vs Run Rate',
          type: 'scatter',
          data: scatterPoints,
          symbolSize: (data: number[]) =>
            Math.max(5, Math.min(30, Math.sqrt(data[2]) * 2)),
          itemStyle: {
            color: (params: any) => {
              const daysUntilStockout = params.data[2]
              if (daysUntilStockout < 10) return colors.danger
              if (daysUntilStockout < 30) return colors.warning
              return colors.success
            },
            opacity: 0.7,
          },
        },
      ],
    }
  }, [data])

  const lineOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          let tooltip = `<div style="padding: 8px;"><strong>${params[0].axisValue}</strong><br/>`
          params.forEach((param: any) => {
            tooltip += `${param.seriesName}: ${param.value.toFixed(2)}<br/>`
          })
          tooltip += '</div>'
          return tooltip
        },
      },
      legend: {
        data: lineChartData.series.map((s) => s.name),
        top: 'top',
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: lineChartData.xAxis,
        axisLabel: {
          interval: 4, // Show every 5th day
        },
      },
      yAxis: {
        type: 'value',
        name: 'Run Rate',
        axisLabel: {
          formatter: '{value}',
        },
      },
      series: lineChartData.series,
    }),
    [lineChartData],
  )

  const scatterOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => `
          <div style="padding: 8px;">
            <strong>${params.data[3]}</strong><br/>
            Current Stock: ${params.data[0]}<br/>
            Daily Run Rate: ${params.data[1].toFixed(2)}<br/>
            Days Until Stockout: ${params.data[2]}
          </div>
        `,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Current Stock',
        axisLabel: {
          formatter: '{value}',
        },
      },
      yAxis: {
        type: 'value',
        name: 'Daily Run Rate',
        axisLabel: {
          formatter: '{value}',
        },
      },
      series: scatterData.series,
    }),
    [scatterData],
  )

  if (loading) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText>Loading inventory data...</DcText>
      </DcDiv>
    )
  }

  if (error) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText color={colors.danger}>Error loading data: {error}</DcText>
      </DcDiv>
    )
  }

  // Show only line chart
  if (showLineChartOnly) {
    return (
      <DcDiv>
        <DcChart
          type="line"
          data={lineChartData}
          options={lineOptions}
          height={300}
          width="100%"
          isLoading={loading}
        />
      </DcDiv>
    )
  }

  // Show only scatter plot
  if (showScatterPlotOnly) {
    return (
      <DcDiv>
        <DcChart
          type="scatter"
          data={scatterData}
          options={scatterOptions}
          height={300}
          width="100%"
          title="Stock vs Run Rate Correlation"
          isLoading={loading}
          isTitleClickable={!!onTitleClick}
          onClickTitle={onTitleClick}
        />
      </DcDiv>
    )
  }

  // Show both (original layout)
  return (
    <DcDiv>
      <DcFlexBox direction="column" gap="20px" height="auto">
        {/* Primary Chart - Line Chart */}
        <DcDiv>
          <DcChart
            type="line"
            data={lineChartData}
            options={lineOptions}
            height={300}
            width="100%"
            title="Daily Run Rate Trends"
            isLoading={loading}
            isTitleClickable={!!onTitleClick}
            onClickTitle={onTitleClick}
          />
        </DcDiv>

        {/* Secondary Chart - Scatter Plot */}
        <DcDiv>
          <DcText
            style={{
              fontSize: '12px',
              color: colors.textSecondary,
              marginBottom: '12px',
            }}
          >
            Bubble size represents days until stockout
          </DcText>
          <DcChart
            type="scatter"
            data={scatterData}
            options={scatterOptions}
            height={300}
            width="100%"
            isLoading={loading}
            onClick={(params: any) => {
              if (onProductClick && data[params.dataIndex]) {
                onProductClick(data[params.dataIndex])
              }
            }}
          />
        </DcDiv>
      </DcFlexBox>
    </DcDiv>
  )
}

export default RunRateChart
